import {
  MarketIntelligenceSourceType,
  MarketIntelligenceTagKind,
  MarketIntelligenceTagSource,
} from '../../interfaces/marketIntelligence'
import { FilterFieldType, IFilter } from './types'

/** Shared with the server's `itemSourceLabel`, so the filter and the column agree. */
export const MARKET_INTELLIGENCE_SOURCE_TYPE_LABEL: Record<string, string> = {
  [MarketIntelligenceSourceType.Manual]: 'Manual',
  [MarketIntelligenceSourceType.FirefliesTranscript]: 'Call',
  [MarketIntelligenceSourceType.SalesforceChatter]: 'Portfolio News',
  [MarketIntelligenceSourceType.MarketNews]: 'Market News',
}

/** What the stored `verbatimQuote` is, per source — it is not always a call. */
export const VERBATIM_QUOTE_LABEL: Record<string, string> = {
  [MarketIntelligenceSourceType.FirefliesTranscript]: 'From the Call',
  [MarketIntelligenceSourceType.SalesforceChatter]: 'From the Post',
  [MarketIntelligenceSourceType.MarketNews]: 'Original Headline',
}

/** Covers Manual and any source added since, where the quote is just the original wording. */
export const VERBATIM_QUOTE_LABEL_FALLBACK = 'Original Wording'

export const MARKET_INTELLIGENCE_TAG_KIND_LABEL: Record<MarketIntelligenceTagKind, string> = {
  [MarketIntelligenceTagKind.Industry]: 'Industry',
  [MarketIntelligenceTagKind.SubIndustry]: 'Sub-industry',
  [MarketIntelligenceTagKind.Client]: 'Client',
  [MarketIntelligenceTagKind.Company]: 'Company',
  [MarketIntelligenceTagKind.Person]: 'Person',
  [MarketIntelligenceTagKind.Organization]: 'Organization',
}

/** Auto-applied tags nobody has confirmed — the tag-correction work queue. */
export const MARKET_INTELLIGENCE_NEEDS_REVIEW = 'Needs Tag Review'

/** The viewer-scoped quick filter, named once so the page can fill in its id. */
export const MARKET_INTELLIGENCE_MINE = 'My Insights'

/**
 * The Captured By SORT target. Split from the filter below the same way My Tasks
 * splits Assignee: the filter compares a uuid, the column has to sort by name.
 * `List` adds this as a select alias so a paginated query can order by it.
 *
 * Mirrors `displayName` (nickname, else first + last, else email) so the column
 * sorts on the string it shows — first name alone left two people who share one
 * in arbitrary order under the default sort.
 *
 * Keep it on ONE line: TypeORM's alias rewrite captures the property name with
 * `[^ =(),]+`, which does not exclude newlines, so a wrapped reference reaches
 * Postgres with the alias still attached. (Same trap as TASK_ASSIGNEE_NAME_SQL.)
 */
export const MARKET_INTEL_AUTHOR_NAME_SQL = `COALESCE(NULLIF(TRIM(author.nickname), ''), NULLIF(TRIM(CONCAT_WS(' ', author.firstName, author.lastName)), ''), author.email)`

export const MARKET_INTELLIGENCE_DEFAULT_SORT = 'capturedByName'

/**
 * Visible columns: Insight, Tagged to, Source, Captured By, Date, and the
 * actions cell. The header row is generated from the same config, so a row
 * rendering a different number of cells shifts every column after the mismatch.
 */
export const MARKET_INTELLIGENCE_COLUMNS_COUNT = 6

export const MARKET_INTELLIGENCE_LIST_FILTERS_CONFIG = [
  {
    field: 'text',
    alias: 'item',
    type: FilterFieldType.Text,
    title: 'Insight',
    placeholder: 'Search insights',
    isFullWidth: true,
  },
  {
    // Scopes the list to one client: items tagged to that client or its company,
    // plus its industry and sub-industry. Options are injected from the loaded
    // clients, the same shape every other client filter in the app uses.
    field: 'clientName',
    type: FilterFieldType.Autocomplete,
    title: 'Client',
    placeholder: 'Client',
    isMultiple: true,
    isCustom: true,
    excludeColumn: true,
    excludeFullSearchFilter: true,
    options: [],
  },
  {
    // One input across every tag on the row: the industry and sub-industry
    // values, the raw name a reference was captured under, and the name of
    // whatever record it resolved to. No single column expresses that.
    field: 'tagSearch',
    type: FilterFieldType.Text,
    title: 'Tagged to',
    placeholder: 'Client, person, org or industry',
    isCustom: true,
  },
  {
    field: 'tagKind',
    type: FilterFieldType.List,
    title: 'Tag type',
    placeholder: 'Tag type',
    isMultiple: true,
    isCustom: true,
    excludeColumn: true,
    options: Object.values(MarketIntelligenceTagKind).map((value) => ({
      value,
      label: MARKET_INTELLIGENCE_TAG_KIND_LABEL[value],
    })),
  },
  {
    // Options are injected at render time from the Salesforce Account
    // Industry_Main_Type__c picklist (see withIndustryOptions). Left empty here
    // so the shared config stays free of a Salesforce round-trip.
    field: 'industry',
    type: FilterFieldType.List,
    title: 'Industry',
    placeholder: 'Industry',
    isMultiple: true,
    isCustom: true,
    excludeColumn: true,
    options: [],
  },
  {
    // Injected alongside `industry`, flattened across every parent — the rail
    // has no selected industry to narrow against the way an edit form does.
    field: 'subIndustry',
    type: FilterFieldType.List,
    title: 'Sub-industry',
    placeholder: 'Sub-industry',
    isMultiple: true,
    isCustom: true,
    excludeColumn: true,
    options: [],
  },
  {
    field: 'tagSource',
    type: FilterFieldType.List,
    title: 'Tagging',
    placeholder: 'Tagging',
    isMultiple: true,
    isCustom: true,
    excludeColumn: true,
    options: [
      { value: MarketIntelligenceTagSource.Auto, label: 'Auto-applied' },
      { value: MarketIntelligenceTagSource.Human, label: 'Confirmed by a person' },
    ],
  },
  {
    field: 'sourceType',
    alias: 'item',
    type: FilterFieldType.List,
    title: 'Source',
    placeholder: 'Source',
    isMultiple: true,
    isSortable: true,
    options: Object.values(MarketIntelligenceSourceType).map((value) => ({
      value,
      label: MARKET_INTELLIGENCE_SOURCE_TYPE_LABEL[value] ?? value,
    })),
  },
  {
    // The Captured By COLUMN — sorts by name, see MARKET_INTEL_AUTHOR_NAME_SQL.
    field: 'capturedByName',
    type: FilterFieldType.Text,
    dbField: MARKET_INTEL_AUTHOR_NAME_SQL,
    isSortable: true,
    excludeFilter: true,
    title: 'Captured By',
  },
  {
    // The Captured By FILTER — a user-id multi-select whose options are injected
    // from the loaded internal users, same as the CRM lists' Owner filter.
    field: 'createdByUserId',
    alias: 'item',
    type: FilterFieldType.Autocomplete,
    title: 'Captured By',
    placeholder: 'Captured By',
    isMultiple: true,
    options: [],
    excludeColumn: true,
  },
  {
    field: 'occurredAt',
    alias: 'item',
    type: FilterFieldType.Date,
    title: 'Date',
    isSortable: true,
    align: 'right',
  },
  {
    // Delete, behind one trigger. A column with no filter behind it, so the
    // generated header row and the rendered cells stay in step.
    field: 'actions',
    type: FilterFieldType.Empty,
    title: '',
    align: 'right',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        // Everything the extractor tagged that no RM has confirmed. The queue
        // the correction flow works through.
        title: MARKET_INTELLIGENCE_NEEDS_REVIEW,
        filters: { tagSource: [MarketIntelligenceTagSource.Auto] },
      },
      {
        title: MARKET_INTELLIGENCE_MINE,
        // `createdByUserId` is filled in at render time with the current user's
        // id (see MarketIntelligencePage's filtersConfig memo).
        filters: { createdByUserId: [] },
      },
    ],
    fields: [
      { field: 'occurredAt', type: FilterFieldType.Date },
      { field: 'tagSource', type: FilterFieldType.List, isMultiple: true },
      { field: 'createdByUserId', type: FilterFieldType.Autocomplete, isMultiple: true },
    ],
  },
] as const satisfies IFilter[]
